import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { TranslateUiModule } from '@core/translation/translate-ui.module';
import { TranslationService } from '@core/translation/translation.service';
import { GoogleMapsService } from '@services/common/google-maps.service';
import { ToasterService } from '@ui-kit/app-toast/toaster.service';
import { OutsideClickModule } from '@ui-kit/directives/outside-click/outside-click.module';

declare const google;

@Component({
  selector: 'google-places-autocomplete',
  templateUrl: './google-places-autocomplete.component.html',
  styleUrls: ['./google-places-autocomplete.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateUiModule, FormsModule, OutsideClickModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GooglePlacesAutocompleteComponent implements OnInit, OnDestroy {
  @Output() emitGoogleAutoComplete: EventEmitter<any> = new EventEmitter();
  @Output() emitEnteredValue: EventEmitter<string> = new EventEmitter();
  autoCompleteAddress: string;
  private _destroyed$ = new Subject();
  defaultPlace: string;
  autoComplete: any;
  isValueMatched = false;
  @Input() set place(data) {
    if (data) {
      this.defaultPlace = data.address;
      this.autoCompleteAddress = data.address;
      this.isValueMatched = true;
    }
  }

  constructor(
    private _renderer2: Renderer2,
    private _toastService: ToasterService,
    private _translateService: TranslationService,
    private _translationService: TranslationService,
    private _googleMapsService: GoogleMapsService
  ) {}

  private initAutocomplete() {
    const input = document.getElementById('placesAutocompleteInput') as HTMLInputElement;
    this.autoComplete = new google.maps.places.Autocomplete(input);

    this.autoComplete.setFields(['address_components', 'geometry', 'name']);
    this.autoComplete.addListener('place_changed', () => {
      const place = this.autoComplete.getPlace();
      if (!place.geometry) {
        // User entered the name of a Place that was not suggested and
        // pressed the Enter key, or the Place Details request failed.
        this._toastService.show({ type: 'error', message: 'No details available for:' + input.value });
        return;
      } else {
        this.emitGoogleAutoComplete.next({ place, address: input.value });
        this.isValueMatched = true;
        return;
      }
    });
  }

  changeResetState() {
    this.isValueMatched = false;
  }

  onChangeInput(value) {
    this.emitEnteredValue.emit(value);
  }

  resetValue(caller: string): void {
    if (!this.isValueMatched || caller === 'clickEvent') {
      this.autoCompleteAddress = '';
      this.isValueMatched = false;

      this.emitEnteredValue.emit('');
    }

    if (caller === 'clickOutsideEvent' && !this.isValueMatched) {
      this.autoCompleteAddress = this.defaultPlace;
      this.isValueMatched = true;
      this.emitEnteredValue.emit(this.defaultPlace);
    }

    if (this.isValueMatched && caller === 'clearEvent') {
      this.autoCompleteAddress = '';
      this.defaultPlace = null;
      this.isValueMatched = false;
      this.emitGoogleAutoComplete.next('');
      this.emitEnteredValue.emit('');
    }
  }

  ngOnInit(): void {
    if (!this._googleMapsService.googleMapsLoaded) {
      const lang = this._translationService.getCurrentLanguage();

      this._googleMapsService.googleMapsLoaded$
        .pipe(
          takeUntil(this._destroyed$),
          filter((val) => !!val),
          map((val) => {
            // @ts-ignore
            if (window.google) {
              this.initAutocomplete();
            }
          })
        )
        .subscribe();
      this._googleMapsService.reloadAutoCompleteScript(lang, this._renderer2);
    } else {
      this.initAutocomplete();
    }
  }

  ngOnDestroy() {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }
}
